import invariant from 'ts-invariant'

export interface IFeatureFlags {
    mobileLock: boolean
    proxyChat: boolean
    connectToLiveAgent: boolean
    webSearch: boolean
    messageExport: boolean
    promptLibrary: boolean
    useAgent: boolean
    news: boolean
    gigiCore: boolean
    imageUnderstanding: boolean
    glossaries: boolean
    mimeTypesNode: boolean
}

type TLocalStorageFlags = Record<string, boolean>

export class FeatureFlags {
    #featureFlags: IFeatureFlags

    constructor(featureFlags: IFeatureFlags) {
        this.#featureFlags = featureFlags
        this.loadFeatureFlags()
    }

    isEnabled(featureName: keyof IFeatureFlags): boolean {
        if (this.#featureFlags[featureName]) {
            return true
        }
        const storedFeatures = localStorage.getItem('overrideFeatures')
        if (!storedFeatures) {
            return false
        }
        const features = JSON.parse(storedFeatures) as Record<string, boolean>
        return features[featureName]
    }

    isKnown(featureName: unknown): featureName is keyof IFeatureFlags {
        return typeof featureName === 'string' && featureName in this.#featureFlags
    }

    verify(featureName: unknown) {
        invariant(typeof featureName === 'string' && featureName in this.#featureFlags && this.isKnown(featureName))
        return featureName
    }

    // below are protected because they are intended to be used only in the browser console for testing
    protected list() {
        const l = Object.keys(this.#featureFlags) as (keyof IFeatureFlags)[]
        console.info(l)
        return l
    }

    protected loadFeatureFlags() {
        const storedFeatures = localStorage.getItem('overrideFeatures')
        const f = storedFeatures ? (JSON.parse(storedFeatures) as TLocalStorageFlags) : {}
        let changed = false

        for (const [name, value] of Object.entries(f)) {
            try {
                const n = this.verify(name)
                this.#featureFlags[n] = !!value
            } catch (_e) {
                console.info(`${name} is not a known feature flag, removing`)
                changed = true
                delete f[name]
            }
        }

        if (changed) {
            localStorage.setItem('overrideFeatures', JSON.stringify(f))
        }
        return f
    }

    protected enable(featureName: unknown): void {
        try {
            const name = this.verify(featureName)
            const features = this.loadFeatureFlags()
            features[name] = true
            localStorage.setItem('overrideFeatures', JSON.stringify(features))
            // could probably set this in the store so it would re-render but I don't care that much
            console.info(`${name} enabled, refresh page to see changes`)
        } catch (_e) {
            console.warn(
                `Warning: the feature ${JSON.stringify(featureName)} is not known to this function, known features are: ${this.list().join(', ')}`,
            )
        }
    }

    protected disable(featureName: unknown): void {
        try {
            const name = this.verify(featureName)
            const features = this.loadFeatureFlags()
            features[name] = false
            localStorage.setItem('overrideFeatures', JSON.stringify(features))
            // could probably set this in the store so it would re-render but I don't care that much
            console.info(`${name} disabled, refresh page to see changes`)
        } catch (_e) {
            console.warn(
                `Warning: the feature ${JSON.stringify(featureName)} is not known to this function, known features are: ${this.list().join(', ')}`,
            )
        }
    }
}

// Attaching enableFeature to the window object, happens in vars.env since that's where they are loaded up
declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        gigiFeatures: FeatureFlags
    }
}
